import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, EMPTY, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Custodian } from '../../../../models/review/custodian.model';
import { CustodianService } from '../../../../services/custodian.service';
import { UserConfigsService } from '../../../../services/user-configs.service';
import { UserConfigs } from '../../../../models/user-configs.model';
import { Role } from '../../../../models/role.model';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { HelpService } from '../../../../services/help.service';
import { CreateCustodianComponent } from '../create-custodian/create-custodian.component';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

@Component({
    selector: 'lex-custodian',
    templateUrl: './custodian.component.html',
    styleUrls: ['./custodian.component.scss'],
    standalone: false
})
export class CustodianComponent implements OnInit, OnDestroy {

	@ViewChild('leftScroll') leftScroll: CdkVirtualScrollViewport;
	@ViewChild('searchBox') searchBoxElement: ElementRef;

	custodians: Custodian[] = [];
	selectedCustodian: Custodian = null;
	hoverFieldId: string = null;
	showEdit: boolean = false;
	showSelect: boolean = false;
	searchText: string = '';

	private searchTerm = new BehaviorSubject<string>('');
	private subscription = new Subscription();

	constructor(
		private userService: UserConfigsService,
		private dialog: MatDialog,
		private snackBar: MatSnackBar,
		private custodianService: CustodianService,
		private helpService: HelpService) { }

	ngOnInit(): void {

		this.selectedCustodian = null;

		this.subscription.add(
			this.searchTerm.pipe(
				// Wait 300ms after each keystroke before considering the term
				debounceTime(300),
				// Ignore new term if same as previous term
				distinctUntilChanged(),
				// Switch to new search observable each time the term changes
				switchMap((term: string) => {
					this.searchText = term;
					if (this.selectedCustodian)
						this.updateCustodianList(this.selectedCustodian.custodianId);
					else
						this.updateCustodianList();
					return EMPTY;
				})
			).subscribe());

		this.setCanEditButton();

		this.helpService.updateHelpLink('analysis-menu/custodians');
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	onUpdateSearchText(term: string): void {
		this.searchTerm.next(term);
	}

	onAddCustodian(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		this.dialog.open(CreateCustodianComponent, dialogConfig).afterClosed().subscribe((custodian: Custodian) => {
			if (custodian) {
				this.custodianService.addNewCustodian(custodian).subscribe(data => {
					this.snackBar.open('Custodian added successfully.', 'X', {
						duration: 3000
					});
					this.updateCustodianList(data.custodianId);
				});
			}
		});
	}

	onDeleteCustodian(custodianId: string): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Custodian`,
			message: `This custodian will be PERMANENTLY DELETED from the entire case.
          <br/> This action cannot be undone.`
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked: boolean) => {
			if (okClicked) {
				this.custodianService.deleteCustodian(custodianId).subscribe(() => {
					this.updateCustodianList();
					this.snackBar.open('Custodian deleted successfully.', 'X', {
						duration: 3000
					});
				});
			}
		});
	}

	updateCustodianList(selectCustodianId?: string): void {
		this.custodianService.getCustodiansWithSearch(this.searchText).subscribe(data => {
			this.custodians = data.value;
			//if selectCustodianId is not empty, set the respective custodian as selected and scroll into view
			if (selectCustodianId) {
				this.selectedCustodian = this.custodians.find(x => x.custodianId === selectCustodianId);
				if (this.selectedCustodian)
					setTimeout(() => this.leftScroll.scrollToIndex(this.custodians.indexOf(this.selectedCustodian)));
			}
			else
				this.selectedCustodian = null;
			if (this.custodians.length > 0) {
				this.showSelect = true;
			}
		});
	}

	private setCanEditButton(): void {
		this.subscription.add(
			this.userService.getUserConfigs().subscribe((userConfig: UserConfigs) =>
				this.showEdit = userConfig.userRoles.some(role => role === Role.EditFeatureSet)
			));
	}
}
